/**
 * Phase 9 — AnalyticsGate consent-driven dispatcher.
 *
 * Mount-once invisible component qui :
 *  1. Lit le consent actuel (`getCookieConsent()`) au mount → applique.
 *  2. S'abonne à `window.cookie-consent-change` (CustomEvent émis par
 *     `<CookieBanner />`) → ré-applique quand l'user accepte/customise.
 *
 * Catégories Loi 25 :
 *  - essential   → toujours actif (cookies session/CSRF, jamais d'analytics)
 *  - analytics   → GA4 + Clarity (heatmaps)
 *  - marketing   → Meta Pixel (conversion tracking)
 *
 * Aucun script tiers ne fire AVANT consent explicit (Loi 25 Art. 9).
 * Tous passent par `idleInject` → injection deferred après idle.
 */
import { useEffect } from 'react';
import { getCookieConsent } from '@/components/ui/cookie-banner';
import type { ConsentCategories } from '@/components/ui/cookie-banner';
import { deferMetaPixel } from './meta-pixel';
import { deferGa4 } from './ga4';
import { deferClarity } from './clarity';
import { deferGhlWidget } from './ghl-widget';

interface ConsentChangeDetail {
  value: string;
  categories: ConsentCategories;
}

function applyConsent(categories: ConsentCategories): void {
  if (categories.analytics) {
    deferGa4();
    deferClarity();
  }
  if (categories.marketing) {
    deferMetaPixel();
    // Phase 27.72C — Codex audit fix : GHL Web Chat Widget aussi consent-gated
    // marketing (collecte name+email+conversation, POST vers serveurs USA).
    // Avant : <script> static dans index.html chargé avant consent (Loi 25 violé).
    deferGhlWidget();
  }
}

export function AnalyticsGate(): null {
  useEffect(() => {
    const consent = getCookieConsent();
    if (consent) applyConsent(consent.categories);

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ConsentChangeDetail>).detail;
      if (detail?.categories) applyConsent(detail.categories);
    };
    window.addEventListener('cookie-consent-change', handler);
    return () => {
      window.removeEventListener('cookie-consent-change', handler);
    };
  }, []);

  return null;
}
